import {
  globalCoverImages,
  PublicImg,
} from '@airship-platform/airship-patterns';
import {
  FileButton,
  FlexBox,
  SectionLoader,
  SelectableCard,
} from '@airship-platform/jam-web/components';
import { CameraIcon } from '@airship-platform/jam-web/icons';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useAppointmentProposalFiles } from '../../../../data-access/appointmentProposalFiles';
import { useProposalHeaderImage } from '../../../../data-access/proposalHeaderImage';

const acceptedFiles = ['image/jpeg', 'image/png', 'image/gif', 'image/jpg'];

interface EditPresentationUploadProps {
  value?: string | null;
  onChange: (
    value: {
      fileId?: string;
      fileType:
        | 'ORG_PROPOSAL_HEADER'
        | 'USER_UPLOAD'
        | 'AIRSHIP_DEFAULT'
        | 'NO_IMAGE';
    } | null
  ) => void;
}

const PHOTO_BOX_SIZE = '150px';

const EditPresentationUpload: React.FC<EditPresentationUploadProps> = ({
  value,
  onChange,
}) => {
  const {
    files,
    uploadProgress,
    isLoading,
    isLoaded,
    isProcessing,
    failedUploads,
    uploadAppointmentFiles,
  } = useAppointmentProposalFiles();

  const { proposalHeaderImages } = useProposalHeaderImage();

  const airshipHeaderImages = useMemo(
    () =>
      globalCoverImages.map((image) => ({
        id: image,
        src: `/airship-proposal-assets/header-img-${image}.png`,
        alt: `Header Img`,
      })),
    []
  );

  const currentFiles = files.filter(
    (file) => file.metadata.uploadCategory === 'PRESENTATION_HEADER'
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const validFiles = Array.from(event.target.files || []).filter((file) =>
      acceptedFiles.includes(file.type)
    );
    if (validFiles.length) {
      uploadAppointmentFiles(validFiles, 'PRESENTATION_HEADER').then((result) =>
        result.forEach((fileId) => {
          onChange({ fileId, fileType: 'USER_UPLOAD' });
        })
      );
    }
  };

  if (isLoading && !isLoaded) {
    return <SectionLoader isLoading title="Fetching current image..." />;
  }

  return (
    <FlexBox flexDirection={'column'} gap={2}>
      <Typography variant="body1">Add cover photo</Typography>
      <FlexBox gap={2} flexWrap="wrap">
        <SelectableCard
          selected={!value}
          onClick={() => onChange({ fileType: 'NO_IMAGE' })}
          width={PHOTO_BOX_SIZE}
          height={PHOTO_BOX_SIZE}
          p={0.5}
        >
          <FlexBox
            alignItems={'center'}
            justifyContent={'center'}
            width={'100%'}
            height={'100%'}
            borderRadius={2}
            border={1}
            borderColor={(theme) => theme.palette.gray?.[200]}
            bgcolor={(theme) => theme.palette.gray?.[100]}
          >
            <Typography variant="body1">None</Typography>
          </FlexBox>
        </SelectableCard>

        {proposalHeaderImages.map((proposalHeaderImage) => {
          const isSelected =
            proposalHeaderImage.proposalHeaderImageId === value;
          return (
            <SelectableCard
              key={proposalHeaderImage.proposalHeaderImageId}
              selected={isSelected}
              width={PHOTO_BOX_SIZE}
              height={PHOTO_BOX_SIZE}
              p={0.5}
              onClick={() =>
                onChange({
                  fileId: proposalHeaderImage.proposalHeaderImageId,
                  fileType: 'ORG_PROPOSAL_HEADER',
                })
              }
            >
              <Box
                component="img"
                src={proposalHeaderImage.image?.url}
                alt={proposalHeaderImage.name}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  border: 1,
                  borderRadius: 2,
                  borderColor: (theme) => theme.palette.gray?.[200],
                }}
              />
            </SelectableCard>
          );
        })}

        {airshipHeaderImages.map((image) => {
          const isSelected = image.id === value;
          return (
            <SelectableCard
              key={image.id}
              selected={isSelected}
              width={PHOTO_BOX_SIZE}
              height={PHOTO_BOX_SIZE}
              p={0.5}
              onClick={() =>
                onChange({ fileId: image.id, fileType: 'AIRSHIP_DEFAULT' })
              }
            >
              <PublicImg
                src={image.src}
                alt={image.alt}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  border: 1,
                  borderRadius: 2,
                  borderColor: (theme) => theme.palette.gray?.[200],
                }}
              />
            </SelectableCard>
          );
        })}

        {currentFiles.map((file) => {
          const isSelected = file.customerFileId === value;
          return (
            <SelectableCard
              key={file.customerFileId}
              selected={isSelected}
              isLoading={isProcessing.includes(file.customerFileId)}
              hasError={failedUploads.includes(file.customerFileId)}
              uploadProgress={uploadProgress[file.customerFileId]}
              onAbort={() => file.uploadAbort?.abort()}
              width={PHOTO_BOX_SIZE}
              height={PHOTO_BOX_SIZE}
              p={0.5}
              onClick={() =>
                onChange({
                  fileId: file.customerFileId,
                  fileType: 'USER_UPLOAD',
                })
              }
            >
              <Box
                component="img"
                src={file.url}
                alt={file.filename}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  border: 1,
                  borderRadius: 2,
                  borderColor: (theme) => theme.palette.gray?.[200],
                }}
              />
            </SelectableCard>
          );
        })}
      </FlexBox>

      <FileButton
        onChange={handleFileChange}
        acceptedFiles={acceptedFiles.join(',')}
        startIcon={<CameraIcon />}
        isLoading={isLoading}
        color="softGold"
      >
        Add photo
      </FileButton>

      {!!failedUploads.length && (
        <Alert severity="error">Upload failed. Please try again.</Alert>
      )}
    </FlexBox>
  );
};

export default EditPresentationUpload;
